import { Panel } from '@/components/panel/panel';
import { Money } from '@/components/text';
import { Avatar } from '@/components/ui/avatar';
import { usePayoutCheckoutStore } from '@/stores/payout-checkout-store';
import { Badge, Box, Flex, Skeleton, SkeletonText, Table, Text } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type CommissionSummaryPanelProps = object;

export const CommissionSummaryPanel: React.FC<CommissionSummaryPanelProps> = () => {
  const { t } = useTranslation();
  const { payout } = usePayoutCheckoutStore();
  const isLoading = !payout;

  const skeletonContent = (
    <>
      <Skeleton height="40px" mb={2} />
      <Skeleton height="40px" mb={2} />
      <Skeleton height="40px" mb={6} />
      <Flex justifyContent="space-between" alignItems="center" w="100%">
        <SkeletonText noOfLines={1} width="150px" />
        <SkeletonText noOfLines={1} width="80px" rootProps={{ alignItems: 'end' }} />
      </Flex>
    </>
  );

  const actualContent = payout && (
    <>
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              {t('payouts.checkout.commissionSummary.table.affiliate')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('payouts.checkout.commissionSummary.table.programs')}
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="end">
              {t('payouts.checkout.commissionSummary.table.commission')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {payout.affiliatePayouts?.map((ap) => {
            const affiliateInfo = payout.involvedAffiliates?.find((ia) => ia.id === ap.affiliateId);
            return (
              <Table.Row key={ap.affiliateId}>
                <Table.Cell display="flex" alignItems="center" gap={2} textStyle="sm">
                  <Avatar
                    src={affiliateInfo?.avatar?.fileUrl ?? undefined}
                    name={affiliateInfo?.name}
                    size="xs"
                  />
                  {affiliateInfo?.name}
                </Table.Cell>
                <Table.Cell>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {ap.programs.map((program) => (
                      <Badge key={program.id} colorPalette="gray" size="sm">
                        {program.name}
                      </Badge>
                    ))}
                  </Box>
                </Table.Cell>
                <Table.Cell textAlign="end">
                  <Money
                    fontSize="sm"
                    fontWeight="normal"
                    value={ap.totalAmount.amount}
                    currency={ap.totalAmount.currencyCode}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="space-between" alignItems="center" mt={4}>
        <Text fontWeight="normal" fontSize="14px">
          {t(
            payout.affiliatePayouts?.length === 1
              ? 'payouts.checkout.commissionSummary.totalSingle'
              : 'payouts.checkout.commissionSummary.total',
            { count: payout.affiliatePayouts?.length || 0 }
          )}
        </Text>
        <Money
          fontSize="14px"
          value={payout.totalCommissionAmount.amount}
          currency={payout.totalCommissionAmount.currencyCode}
          textProps={{ fontWeight: 'medium', fontSize: '14px' }}
        />
      </Flex>
    </>
  );

  return (
    <Panel title={t('payouts.checkout.commissionSummary.title')}>
      {isLoading ? skeletonContent : actualContent}
    </Panel>
  );
};
